import { create } from "zustand";

import { optInPosthogCapturing } from "@/analytics/posthogConsent";

import {
  cleanupDeclinedStorage,
  getConsent,
  hasStoredChoice,
  storeConsent,
  type Consent,
  type ConsentChoice,
  type StoredConsentSource,
} from "@/consent/consent";
import {
  sendConsentMetric,
  type ConsentMetricAction,
} from "@/consent/consentMetric";

type ConsentStore = {
  consent: Consent;
  bannerOpen: boolean;
  prefsOpen: boolean;

  initBanner: () => void;
  openPrefs: () => void;
  closePrefs: () => void;
  acceptAll: () => void;
  dismissBanner: () => void;
  savePreferences: (choice: ConsentChoice) => void;
};

export const useConsentStore = create<ConsentStore>()((set, get) => {
  const applyChoice = (
    choice: ConsentChoice,
    source: StoredConsentSource,
    action: ConsentMetricAction,
  ) => {
    const previous = get().consent;
    const revoked =
      (previous.analytics && !choice.analytics) ||
      (previous.marketing && !choice.marketing);

    const consent = storeConsent(choice, source);
    cleanupDeclinedStorage(choice);
    sendConsentMetric(action, choice);
    set({ consent, bannerOpen: false, prefsOpen: false });

    if (revoked) {
      // Already-running scripts can't be unloaded; a fresh load never starts them.
      window.location.reload();
      return;
    }
    if (!previous.analytics && choice.analytics) {
      optInPosthogCapturing();
    }
  };

  return {
    consent: getConsent(),
    bannerOpen: false,
    prefsOpen: false,

    initBanner: () => set({ bannerOpen: !hasStoredChoice() }),
    openPrefs: () => set({ prefsOpen: true }),
    closePrefs: () => set({ prefsOpen: false }),
    acceptAll: () =>
      applyChoice({ analytics: true, marketing: true }, "explicit", "accepted"),
    dismissBanner: () => {
      const { analytics, marketing } = get().consent;
      applyChoice({ analytics, marketing }, "acknowledged", "dismissed");
    },
    savePreferences: (choice) => applyChoice(choice, "explicit", "customized"),
  };
});
